import { BROWSER_STORAGE_KEYS } from '@constants';
import { updateRefScrollPosition } from './handleScroll';
import { 
    selectInfoItem, 
    setPresentationType,
    setSlideId, 
    setStepsReviewed, 
    setPdfPage, 
    setShowPointer, 
    setPresentationEnded,
    setViewport,
    setClientConnected, 
    setPresentationId,
    setLastSlideId
} from '@actions/creators';

const { 
    slideIdKey,
    lastSlideIdKey, 
    checkboxKey, 
    presentationTypeKey, 
    pdfPageKey, 
    pointerKey, 
    presentationEndedKey,
    scrollPositionKey,
    stepsReviewedKey,
    loanCalculationsKey,
    clientConnectionKey,
    viewportKey,
    presentationIdKey } = BROWSER_STORAGE_KEYS;

export const validateStorageKey = (storageKey) => {
    if (!storageKey) {
        console.warn(`Storage key not found. Key: ${storageKey}`);
        return false;
    } else {
        return true;
    }
};

export const clearBrowserStorage = (browserStorage = localStorage) => {
    browserStorage.clear();
};

export const endPresentation = (dispatch, browserStorage = localStorage) => {
    dispatch(setPresentationEnded(true));
    setBrowserStorageItem(presentationEndedKey, true, browserStorage);
};

export const getBrowserStorageItem = (storageKey, browserStorage = localStorage) => {
    if (validateStorageKey(storageKey)) {
        const savedItem = browserStorage.getItem(storageKey);

        return savedItem ? JSON.parse(savedItem) : null;
    }
};

export const setBrowserStorageItem = (storageKey, newValue, browserStorage = localStorage) => {
    if (validateStorageKey(storageKey)) {
        browserStorage.setItem(storageKey, JSON.stringify(newValue));
    }
};

export const updateSavedSteps = (slideId, step) => {
    const slideStepsKey = `${stepsReviewedKey}_${slideId}`;
    
    const savedSteps = getBrowserStorageItem(slideStepsKey);

    if (savedSteps) {
        setBrowserStorageItem(slideStepsKey, [...savedSteps, step]);
    } else {
        setBrowserStorageItem(slideStepsKey, [step]);
    }
};

export const retrieveSavedSteps = (slideId, dispatch) => {
    const key = `${stepsReviewedKey}_${slideId}`;
    const savedSteps = getBrowserStorageItem(key);

    if (savedSteps) {
        dispatch(setStepsReviewed(slideId, savedSteps));
    }
};

export const updateSavedLoanCalculations = (name, value) => {
    const savedCalculations = getBrowserStorageItem(loanCalculationsKey);

    if (savedCalculations) {
        setBrowserStorageItem(loanCalculationsKey, { ...savedCalculations, [name]: value });
    } else {
        setBrowserStorageItem(loanCalculationsKey, { [name]: value });
    }
};

export const retrieveScrollPosition = (scrollId, ref) => {
    const storageKey = `${scrollPositionKey}_${scrollId}`;
    const savedScrollPosition = getBrowserStorageItem(storageKey);
    
    if (savedScrollPosition) {
        const direction = savedScrollPosition.hasOwnProperty('scrollTop') ? 'scrollTop' : 'scrollLeft';

        updateRefScrollPosition(ref, savedScrollPosition[direction], direction);
    } else if (scrollId.includes('slide-body') && savedScrollPosition === null) {
        // Reset scroll to top to avoid overrides for null values
        updateRefScrollPosition(ref, 0, 'scrollTop');
        setBrowserStorageItem(storageKey, { scrollTop: 0 });
    }
};

export const retrieveSavedData = (storageKeys, dispatch, browserStorage = localStorage) => {
    if (!Array.isArray(storageKeys)) {
        console.warn(`Provided keys must be in array format. Received: ${typeof storageKeys}`);
        return null;
    } else if (!storageKeys.length) {
        console.warn('Storage keys cannot be empty');
        return null;
    } else {
        const savedData = [];
        const noData = [];

        storageKeys.forEach(key => {
            const item = getBrowserStorageItem(key, browserStorage);

            if (item !== null) {
                savedData.push({ key, item });
            } else {
                noData.push(key);
            }
        });

        if (noData.length) {
            console.log(`Did not find data for storage keys: ${JSON.stringify(noData)}`);
        }

        if (savedData.length) {            
            savedData.forEach(data => {
                const { key, item } = data;

                updateStore(key, item, dispatch);
            });
        } else {
            console.log(`Did not find any stored presentation data in ${browserStorage === localStorage ? 'local' : 'browser'} storage, which indicates that it was recently cleared.`);
        }
    }
};

export function updateStore(storageKey, storageValue, dispatch) {
    switch(storageKey) {
        case clientConnectionKey:
            dispatch(setClientConnected(storageValue));
            dispatch(setPresentationEnded(false));
            break;
        case slideIdKey:
            dispatch(setSlideId(storageValue));
            break;
        case lastSlideIdKey:
            dispatch(setLastSlideId(storageValue));
        case presentationTypeKey:
            dispatch(setPresentationType(storageValue));
            break;
        case checkboxKey:
            dispatch(selectInfoItem(storageValue));
            break;
        case pdfPageKey:
            dispatch(setPdfPage(storageValue));
            break;
        case pointerKey:
            dispatch(setShowPointer(storageValue));
            break;
        case presentationEndedKey:
            dispatch(setPresentationEnded(storageValue));
            break;
        case viewportKey:
            dispatch(setViewport(storageValue));
            break;
        case presentationIdKey:
            dispatch(setPresentationId(storageValue));
        default:
            console.log(`Could not find an expected operation for storage key ${storageKey}`);
    }
}